/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../../shared/Avatar";
import { shiftDate } from "../../shared/dates";
import type { Member, RotaSettings } from "../../Team/types";
import { autoDraft, type DraftResult, type Preference } from "../engine/autodraft";
import { dayNumber, type Absence } from "../engine/rules";
import type { Shift } from "./types";

type Props = {
  weekStart: string;
  members: Member[];
  settings: RotaSettings;
  onClose: (changed: boolean) => void;
};

type Pref = { staff_id: number; desired_hours: number | null; weekdays: number[] | null; dates: string[] | null; submitted_at: string | null };

const monthOf = (iso: string) => `${iso.slice(0, 7)}-01`;
const monthEnd = (iso: string) => {
  const d = new Date(`${monthOf(iso)}T12:00:00`);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 12).toISOString().slice(0, 10);
};

// "Generate a draft, not a decree": choose the range and the people, see a
// first draft with its gaps, then apply it (as drafts) or go back.
export const AutoDraftModal = defineComponent<Props>({
  name: "AutoDraftModal",
  props: ["weekStart", "members", "settings", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const scope = ref<"week" | "month">("week");
    const chosen = ref<number[]>(props.members.filter((m) => m.active).map((m) => m.id));
    const fillFirst = ref(true);
    const prefs = ref<Pref[]>([]);
    const busy = ref(false);
    // Filling from templates already changed the rota, even if the draft isn't applied.
    const filledSlots = ref(false);
    const preview = ref<{ result: DraftResult; slots: Shift[]; targets: Map<number, number | null> } | null>(null);

    const range = computed(() =>
      scope.value === "week"
        ? { from: props.weekStart, to: shiftDate(props.weekStart, 6) }
        : { from: monthOf(props.weekStart), to: monthEnd(props.weekStart) },
    );
    const month = computed(() => monthOf(props.weekStart));
    const monthLabel = computed(() => new Date(`${month.value}T12:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" }));

    onMounted(async () => {
      prefs.value = (await trpc.rota.preferences.month.query({ month: month.value })) as Pref[];
    });
    const prefFor = (id: number) => prefs.value.find((p) => p.staff_id === id);

    const toggle = (id: number) => (chosen.value = chosen.value.includes(id) ? chosen.value.filter((x) => x !== id) : [...chosen.value, id]);

    const generate = async () => {
      busy.value = true;
      try {
        if (fillFirst.value) {
          const { added } = await trpc.rota.shifts.fillFromTemplates.mutate(range.value);
          if (added) filledSlots.value = true;
        }
        const around = { from: shiftDate(range.value.from, -1), to: shiftDate(range.value.to, 1) };
        const [shifts, off] = await Promise.all([trpc.rota.shifts.list.query(around), trpc.rota.timeOff.list.query(around)]);
        const all = shifts as Shift[];
        const slots = all.filter((s) => s.status === "draft" && s.staff_id == null && s.shift_date >= range.value.from && s.shift_date <= range.value.to);
        const days = dayNumber(range.value.to) - dayNumber(range.value.from) + 1;
        const daysInMonth = dayNumber(monthEnd(props.weekStart)) - dayNumber(month.value) + 1;
        const people = props.members.filter((m) => chosen.value.includes(m.id));
        const targets = new Map<number, number | null>();
        const preferences: Preference[] = people.map((m) => {
          const p = prefFor(m.id);
          const target =
            p?.desired_hours != null
              ? (p.desired_hours * days) / daysInMonth
              : m.contract_hours_week != null
                ? (m.contract_hours_week * days) / 7
                : null;
          targets.set(m.id, target);
          return { staff_id: m.id, target_hours: target, weekdays: p?.weekdays ?? [], dates: p?.dates ?? [] };
        });
        const result = autoDraft({
          shifts: all,
          slotIds: slots.map((s) => s.id),
          people,
          preferences,
          absences: (off as (Absence & { status: string })[]).filter((o) => o.status === "approved"),
          settings: props.settings,
        });
        preview.value = { result, slots, targets };
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.load_failed"), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const apply = async () => {
      if (!preview.value) return;
      busy.value = true;
      try {
        const { assigned } = await trpc.rota.shifts.assignBatch.mutate({ assignments: preview.value.result.assignments });
        toast.showToast(t("workday.auto.applied").replace("{count}", String(assigned)), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const renderSetup = () => (
      <div class="space-y-5 p-6">
        <div class="flex gap-1 rounded-xl bg-base-200 p-1">
          {(["week", "month"] as const).map((s) => (
            <button key={s} class={["btn btn-sm flex-1", scope.value === s ? "bg-base-100 shadow-sm" : "btn-ghost"].join(" ")} onClick={() => (scope.value = s)}>
              {s === "week" ? t("workday.auto.this_week") : monthLabel.value}
            </button>
          ))}
        </div>
        <div>
          <div class="mb-1 text-lg font-semibold">{t("workday.auto.select_team")}</div>
          <div class="mb-2 text-xs text-base-content/60">{t("workday.auto.selected").replace("{count}", String(chosen.value.length))}</div>
          <ul class="space-y-2">
            {props.members.filter((m) => m.active).map((m) => {
              const on = chosen.value.includes(m.id);
              const p = prefFor(m.id);
              return (
                <li key={m.id}>
                  <button class={["flex w-full items-center gap-3 rounded-xl border p-3 text-left", on ? "border-success/60 bg-success/5" : "border-base-300"].join(" ")} onClick={() => toggle(m.id)}>
                    <Avatar name={m.name} id={m.id} color={m.color} size="sm" />
                    <span class="min-w-0 flex-1">
                      <span class="block text-sm font-medium">{m.name}</span>
                      <span class={["block text-xs", p?.submitted_at ? "text-base-content/60" : "text-warning"].join(" ")}>
                        {p?.submitted_at ? t("workday.requests.prefs_submitted") : t("workday.requests.prefs_missing")}
                      </span>
                    </span>
                    <span class={["flex h-6 w-6 items-center justify-center rounded-full border", on ? "border-success bg-success text-success-content" : "border-base-300"].join(" ")}>
                      {on && <span class="material-symbols-outlined text-sm">check</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <label class="flex items-center gap-2 text-sm">
          <input type="checkbox" class="checkbox checkbox-sm" v-model={fillFirst.value} />
          {t("workday.auto.fill_first")}
        </label>
        <p class="text-xs text-base-content/60">{t("workday.auto.explain")}</p>
      </div>
    );

    const renderPreview = () => {
      const p = preview.value!;
      const filled = p.result.assignments.length;
      const total = p.slots.length;
      const name = (id: number) => props.members.find((m) => m.id === id)?.name ?? "?";
      const slotById = new Map(p.slots.map((s) => [s.id, s]));
      return (
        <div class="space-y-5 p-6">
          <div class="rounded-2xl bg-base-200 p-5">
            <div class="text-sm text-base-content/60">{t("workday.auto.draft_preview")}</div>
            <div class="text-3xl font-semibold">{t("workday.auto.filled").replace("{filled}", String(filled)).replace("{total}", String(total))}</div>
            <progress class="progress progress-success mt-2 h-2 w-full" value={filled} max={total || 1} />
          </div>
          <div>
            <div class="mb-2 text-sm font-semibold">{t("workday.auto.hours")}</div>
            <ul class="space-y-2">
              {props.members.filter((m) => chosen.value.includes(m.id)).map((m) => {
                const hrs = p.result.hours.get(m.id) ?? 0;
                const target = p.targets.get(m.id);
                return (
                  <li key={m.id} class="flex items-center gap-2 text-sm">
                    <Avatar name={m.name} id={m.id} color={m.color} size="xs" />
                    <span class="flex-1">{m.name}</span>
                    <span class="tabular-nums text-base-content/70">{`${hrs.toFixed(1)}h${target != null ? ` / ${target.toFixed(0)}h` : ""}`}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          {p.result.unfilled.length > 0 && (
            <div>
              <div class="mb-2 text-sm font-semibold text-warning">{t("workday.auto.gaps").replace("{count}", String(p.result.unfilled.length))}</div>
              <ul class="max-h-48 space-y-1 overflow-auto text-xs">
                {p.result.unfilled.map((u) => {
                  const s = slotById.get(u.shift_id)!;
                  return (
                    <li key={u.shift_id} class="flex justify-between gap-2 rounded-lg bg-warning/10 px-2 py-1">
                      <span>
                        {new Date(`${s.shift_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} {`${s.start_time}–${s.end_time}`} · {s.role_name ?? "—"}
                      </span>
                      <span class="text-base-content/60">{t(`workday.auto.reason_${u.reason}`)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {filled > 0 && (
            <details class="text-xs text-base-content/60">
              <summary class="cursor-pointer">{t("workday.auto.show_assignments")}</summary>
              <ul class="mt-2 space-y-0.5">
                {p.result.assignments.map((a) => {
                  const s = slotById.get(a.shift_id)!;
                  return (
                    <li key={a.shift_id}>
                      {s.shift_date} {`${s.start_time}–${s.end_time}`} · {s.role_name} → <span class="font-medium text-base-content">{name(a.staff_id)}</span>
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={t("workday.auto.title")}
        onClose={() => props.onClose(filledSlots.value)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => (preview.value ? (preview.value = null) : props.onClose(filledSlots.value))}>
                {preview.value ? t("workday.auto.back") : t("general.words.cancel")}
              </button>
              {preview.value ? (
                <button class="btn btn-primary" disabled={busy.value || !preview.value.result.assignments.length} onClick={apply}>
                  {t("workday.auto.apply")}
                </button>
              ) : (
                <button class="btn btn-primary" disabled={busy.value || !chosen.value.length} onClick={generate}>
                  {busy.value ? <span class="loading loading-spinner loading-sm" /> : <span class="material-symbols-outlined text-base">auto_awesome</span>}
                  {t("workday.auto.generate")}
                </button>
              )}
            </div>
          </div>
        )}
      >
        {busy.value && !preview.value ? (
          <div class="flex flex-col items-center gap-3 p-10">
            <Loading />
            <span class="text-sm text-base-content/60">{t("workday.auto.working")}</span>
          </div>
        ) : preview.value ? (
          renderPreview()
        ) : (
          renderSetup()
        )}
      </LemmonModal>
    );
  },
});
